import React from 'react';
import { SVGProps } from 'react';
import { cn } from '@/utils/utils';
import { iconRegistry, IconName } from './IconRegistry';
import { IconSizes, IconColors, IconSize, IconColor } from './IconUtils';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  name: IconName;
  size?: number | string | IconSize;
  color?: string | IconColor;
  className?: string;
  variant?: 'filled' | 'outline' | 'ghost';
  hover?: boolean;
}

export const Icon: React.FC<IconProps> = ({
  name,
  size = 24,
  color = 'currentColor',
  className,
  variant = 'filled',
  hover = false,
  ...props
}) => {
  const iconData = iconRegistry[name];
  
  if (!iconData) {
    console.warn(`Icon "${name}" not found in icon registry`);
    return null;
  }

  // Resolve size from preset or use as-is
  const resolvedSize = typeof size === 'string' && size in IconSizes 
    ? IconSizes[size as IconSize] 
    : size;

  // Resolve color from preset or use as-is
  const resolvedColor = typeof color === 'string' && color in IconColors 
    ? IconColors[color as IconColor] 
    : color;

  // Generate variant classes
  const variantClasses = {
    filled: '',
    outline: 'stroke-current fill-none',
    ghost: 'opacity-60'
  };

  const hoverClasses = hover ? 'transition-colors duration-200 hover:opacity-80' : '';

  return (
    <svg
      width={resolvedSize}
      height={resolvedSize}
      viewBox={iconData.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        "inline-block flex-shrink-0",
        variantClasses[variant],
        hoverClasses,
        className
      )}
      {...props}
    >
      {iconData.paths.map((path: string, index: number) => (
        <path
          key={index}
          d={path}
          fill={variant === 'outline' ? 'none' : resolvedColor}
          stroke={variant === 'outline' ? resolvedColor : 'none'}
          strokeWidth={variant === 'outline' ? 2 : 0}
        />
      ))}
    </svg>
  );
};

export default Icon; 